import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation, useOutletContext } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { getArticleById, deleteArticle } from '../services/articleService';
import PageFooter from '../components/PageFooter';
import './ArticleDetailPage.css';

const ArticleDetailPage = () => {
  const { articleId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useOutletContext() ?? {};
  const { lang, t } = useLanguage();
  const s = t.articles;

  const [article, setArticle] = useState(location.state?.article ?? null);
  const [isLoading, setIsLoading] = useState(!location.state?.article);
  const [error, setError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const isAdmin = currentUser?.role === 'ADMIN';
  const articlesPath = lang === 'he' ? '/heb/articles' : '/articles';

  useEffect(() => {
    if (article?.content) return;

    const controller = new AbortController();
    setIsLoading(true);
    getArticleById(articleId, controller.signal)
      .then((data) => setArticle(data))
      .catch((err) => {
        if (err.name !== 'AbortError') setError(s.errorLoad);
      })
      .finally(() => setIsLoading(false));

    return () => controller.abort();
  }, [articleId, article?.content, s.errorLoad]);

  const handleBack = () => {
    navigate(articlesPath);
  };

  const handleEdit = () => {
    const id = article._id || article.id;
    navigate(
      lang === 'he' ? `/heb/admin/articles/${id}/edit` : `/admin/articles/${id}/edit`,
      { state: { article } }
    );
  };

  const handleDelete = async () => {
    if (!window.confirm(s.confirmDelete)) return;
    setIsDeleting(true);
    try {
      await deleteArticle(article._id || article.id);
      navigate(articlesPath);
    } catch {
      alert(s.errorDelete);
      setIsDeleting(false);
    }
  };

  const handleShare = () => {
    const url =
      window.location.origin +
      window.location.pathname +
      (lang === 'he'
        ? `#/heb/articles/${article._id || article.id}`
        : `#/articles/${article._id || article.id}`);
    const text = article.title + (article.summary ? ' — ' + article.summary : '');

    if (navigator.share) {
      navigator.share({ title: article.title, text, url });
    } else {
      const wa = `https://wa.me/?text=${encodeURIComponent(text + '\n' + url)}`;
      window.open(wa, '_blank');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString(lang === 'he' ? 'he-IL' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="article-detail-page">
        <main className="article-detail-main">
          <p className="article-detail-status">{s.loading}</p>
        </main>
        <PageFooter />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="article-detail-page">
        <main className="article-detail-main">
          <p className="article-detail-status article-detail-status--error">
            {error || s.notFound}
          </p>
          <button className="article-back-btn" onClick={handleBack}>
            ← {s.backLabel}
          </button>
        </main>
        <PageFooter />
      </div>
    );
  }

  return (
    <div className="article-detail-page">
      <main className="article-detail-main">
        {/* Back link */}
        <button className="article-back-btn" onClick={handleBack}>
          ← {s.backLabel}
        </button>

        {/* Hero image */}
        {article.imageUrl && (
          <div className="article-detail-hero-image">
            <img src={article.imageUrl} alt={article.title} />
          </div>
        )}

        {/* Meta */}
        <div className="article-detail-meta">
          {article.tags?.length > 0 && (
            <div className="article-detail-tags">
              {article.tags.map((tag) => (
                <span key={tag} className="article-detail-tag">
                  {tag}
                </span>
              ))}
            </div>
          )}
          <h1 className="article-detail-title">{article.title}</h1>
          {article.summary && <p className="article-detail-summary">{article.summary}</p>}
          <div className="article-detail-byline">
            {article.authorName && (
              <span>
                {s.by} {article.authorName}
              </span>
            )}
            {article.createdAt && <span>{formatDate(article.createdAt)}</span>}
          </div>
        </div>

        {/* Admin actions */}
        {isAdmin && (
          <div className="article-detail-admin-actions">
            <button className="article-admin-action-btn" onClick={handleEdit}>
              {s.editArticle}
            </button>
            <button
              className="article-admin-action-btn article-admin-action-btn--delete"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {s.deleteArticle}
            </button>
            <button className="article-admin-action-btn article-admin-action-btn--share" onClick={handleShare}>
              {s.shareLabel}
            </button>
          </div>
        )}

        {/* Content */}
        <div className="article-detail-content">
          {(article.content || '').split('\n').map((para, i) =>
            para.trim() ? (
              <p key={i}>{para}</p>
            ) : (
              <br key={i} />
            )
          )}
        </div>

        {/* Share button (non-admin) */}
        {!isAdmin && (
          <button className="article-share-btn" onClick={handleShare}>
            {s.shareLabel}
          </button>
        )}
      </main>
      <PageFooter />
    </div>
  );
};

export default ArticleDetailPage;
